import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { isNull } from 'lodash';
import { memo, useState, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useRouter } from 'next/router';
import ErrorTooltip from '../../../ErrorTooltip';
import ToolTip from '../../../ToolTip';
import { SearchStatus, SearchModes } from '../../../../constants';
import {
  tourSearchRequest,
  resetSearchResults,
  setTourSearchObjFromQueryString,
  setTourTripSearchParam,
} from '../../../../store/slices/tripSearch';
import { resetTrips } from '../../../../store/slices/trip';
import setURLParams from '../../../../utils/setURLParams';
import useSearchObjFromQueryString from '../../../../hooks/useSearchObjFromQueryString';
import TourDestinationSearch from '../../../TravelOption/TourInformation/TourFilters/TourDestinationSearch';
import TourStartDateFilter from '../../../TravelOption/TourInformation/TourFilters/TourStartDateFilter';
import TourEndDateFilter from '../../../TravelOption/TourInformation/TourFilters/TourEndDateFilter';
import checkDoEnableTripConfigDecoupling from '../../../../helpers/selectors/checkDoEnableTripConfigDecoupling';

const searchedFields = ['country', 'region', 'town', 'text', 'startDate', 'endDate'];

const checkIsSearchChanged = (inputs, searched) =>
  searchedFields.some((field) => (inputs?.[field] || null) !== (searched?.[field] || null));

const TourSearchBar = ({ tripId }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const reduxStore = useStore();
  const [doOpenTourStartDate, setDoOpenTourStartDate] = useState(false);
  const [doOpenTourEndDate, setDoOpenTourEndDate] = useState(false);
  const initialFormValidations = { startDate: null, endDate: null, destination: null };
  const [formValidations, setFormValidations] = useState(initialFormValidations);
  const { searchMode: searchModeQuery } = router.query;
  const selectedSearchMode = useSelector((store) => store.tripSearch.selectedSearchMode);
  const { isLoggedIn } = useSelector((store) => store.user);
  const { searchInputs, searchObj, searchStatus } = useSelector((store) => store.tripSearch);
  const doEnableTripConfigDecoupling = useSelector(checkDoEnableTripConfigDecoupling);
  const [doRemindToSearch, setDoRemindToSearch] = useState(false);

  useEffect(() => {
    setDoRemindToSearch(
      searchStatus === SearchStatus.DONE && checkIsSearchChanged(searchInputs.tour, searchObj.tour),
    );
  }, [searchInputs, searchObj, searchStatus]);

  const handleOpenFirstDatePicker = (val) => {
    setDoOpenTourStartDate(val);
    if (!val) {
      setTimeout(() => {
        setDoOpenTourEndDate(true);
      }, 50);
    }
  };

  useEffect(() => {
    if (doOpenTourStartDate) setDoOpenTourEndDate(false);
  }, [doOpenTourStartDate]);

  const validateSearch = (obj, doShowValidationErrors = true) => {
    const validationObj = { ...initialFormValidations };
    let isValid = true;
    if (!obj) return false;
    if (obj.text) return true;
    if (!obj.startDate) {
      validationObj.checkInDate = 'A first date is required.';
      isValid = false;
    } else if (!obj.endDate) {
      validationObj.checkOutDate = 'A last date is required.';
      isValid = false;
    } else if (dayjs(obj.endDate).isBefore(dayjs(obj.startDate), 'day')) {
      validationObj.checkOutDate = 'The last date cannot be before the first one.';
      isValid = false;
    } else if (!obj.country) {
      validationObj.destination = 'Destination is required';
      isValid = false;
    }
    if (doShowValidationErrors) setFormValidations(validationObj);
    return isValid;
  };

  const handleSearch = (event, doShowValidationErrors = true) => {
    if (event) event.preventDefault();
    const submittedSearchInputs = reduxStore.getState().tripSearch.searchInputs;
    if (validateSearch(submittedSearchInputs.tour, doShowValidationErrors)) {
      dispatch(resetTrips({ doEnableTripConfigDecoupling }));
      dispatch(resetSearchResults());
      dispatch(setTourTripSearchParam({ ...submittedSearchInputs.tour, page: 1 }));
      dispatch(tourSearchRequest({ isTripSearch: true }));
      setURLParams({
        searchObj: submittedSearchInputs,
        searchedMode: SearchModes.TOUR,
        router,
      });
    }
  };

  const handleResetValidation = () => {
    setFormValidations(initialFormValidations);
  };

  useSearchObjFromQueryString((searchObjFromQueryString) => {
    if (searchStatus !== SearchStatus.INITIAL) return;
    if ((searchModeQuery || SearchModes.CRUISE_TRIP) === selectedSearchMode) {
      dispatch(setTourSearchObjFromQueryString(searchObjFromQueryString));
      if (validateSearch(searchObjFromQueryString, false)) {
        dispatch(resetTrips());
        dispatch(tourSearchRequest({ isTripSearch: true }));
      }
    } else {
      dispatch(resetSearchResults({ isLoggedIn }));
      handleSearch(null, false);
    }
  }, undefined);

  return (
    <form onSubmit={handleSearch} onFocus={handleResetValidation}>
      <div>
        <div className="grid xl:grid-cols-10 md:grid-cols-12 grid-cols-1 mt-2 px-0 py-2 md:p-0 gap-y-2">
          <ErrorTooltip
            title={formValidations.destination || ''}
            open={!isNull(formValidations.destination)}
            arrow
          >
            <div className="xl:col-span-4 col-span-4">
              <TourDestinationSearch tripId={tripId} />
            </div>
          </ErrorTooltip>
          <ErrorTooltip
            title={formValidations.checkInDate || ''}
            open={!isNull(formValidations.checkInDate)}
            arrow
          >
            <div className="xl:col-span-2 col-span-3">
              <TourStartDateFilter
                doOpenDatePicker={doOpenTourStartDate}
                setDoOpenDatePicker={handleOpenFirstDatePicker}
                tripId={tripId}
              />
            </div>
          </ErrorTooltip>
          <ErrorTooltip
            title={formValidations.checkOutDate || ''}
            open={!isNull(formValidations.checkOutDate)}
            arrow
          >
            <div className="xl:col-span-2 col-span-3">
              <TourEndDateFilter
                doOpenDatePicker={doOpenTourEndDate}
                setDoOpenDatePicker={setDoOpenTourEndDate}
                tripId={tripId}
              />
            </div>
          </ErrorTooltip>
          <div className="col-span-2">
            <ToolTip
              zIndex={1}
              title="Please click Search to apply the newly changed parameters"
              open={doRemindToSearch}
              onClose={() => setDoRemindToSearch(false)}
              placement="top"
            >
              <button
                type="submit"
                className="md:rounded-r-2xl md:rounded-l-none rounded-l-2xl rounded-r-2xl bg-searchButton-0 text-searchbtn-0 font-bold text-center w-full h-16 md:h-full"
              >
                Search
              </button>
            </ToolTip>
          </div>
        </div>
      </div>
    </form>
  );
};

TourSearchBar.propTypes = {
  tripId: PropTypes.string,
};

TourSearchBar.defaultProps = {
  tripId: undefined,
};

export default memo(TourSearchBar);
